import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AccessService } from '@app/core/access/access.service';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';
import { KendoGridComponent } from '@app/shared/kendo/kendo-grid.component';
import { KendoGridService } from '@app/shared/kendo/kendo-grid.service';
import { environment } from '@env/environment';
import { AuthenticationService, Logger } from '@app/core';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { DataStateChangeEvent, GridDataResult, RowArgs, SelectableSettings } from '@progress/kendo-angular-grid';
import { ApiQuery } from '@app/core/http/api-query';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { DataSet } from '@app/shared/kendo/data-set';
import { CustomKendoFilter } from '@app/shared/kendo/custom-kendo-filter';
import { SalesService } from '@app/sales/sales/sales.service';
import { CampaignService } from '@app/campaign/campaign.service';
import { Sale } from '@app/sales/sales/sale.model';
import * as moment from 'moment';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TemplatesService } from '@app/settings/templates/templates.service';
import { EmailTemplate } from '@app/settings/templates/email-template.model';
import { Company } from '@app/settings/companies/company.model';
import { Locale } from '@app/settings/locales/locales.model';
import { EmailTemplateCompiler } from '@app/settings/templates/email-template-compiler.model';
import { SmtpSettings } from '@app/shared/components/smtp-settings/smtp-settings.model';
import { Templates } from '@app/settings/types/templates.enum';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { Campaign } from '../campaign.model';
import { CssSelector } from '@angular/compiler';
import { CdkFixedSizeVirtualScroll } from '@angular/cdk/scrolling';
import { SaleFinal } from '@app/sales/sales/sale-final.model';

const log = new Logger('CampaignCreateComponent');

@Component({
  selector: 'campaign-create',
  templateUrl: './campaign-create.component.html',
  styleUrls: ['./campaign-create.component.scss'],
  // Вътрешните също трябва да имат changeDetection...
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignCreateComponent extends KendoGridComponent implements OnInit {
  requestsResponse: PaginatedResponse;
  data: GridDataResult;
  state: DataStateChangeEvent;
  apiQuery: ApiQuery = new ApiQuery();
  env = environment;
  eventGroupId: number;
  AuthenticationService = AuthenticationService;

  UserRoleType = UserRoleType;
  LaravelModelsEnum = LaravelModelsEnum;

  loader = false;
  currentStep = 1;
  selectedSales: Array<Sale> = [];
  selectedSalesIds: Array<number> = [];
  selectableSettings: SelectableSettings = {
    checkboxOnly: true,
    mode: 'multiple',
  };

  form: FormGroup;
  filterForm: FormGroup;
  templateTypeId = 177;
  templates: Array<EmailTemplate> = [];
  filteredTemplates: Array<EmailTemplate> = [];
  companies: Array<Company> = [];
  locales: Array<Locale> = [];
  files: any = {
    uploads: [],
  };
  senderSmtpSetting: Array<SmtpSettings> = [];
  template_types: Array<any> = [];
  templatesTypes = Templates;
  selectedEvent: Event = null;
  emptySale: Sale = new Sale();
  campaignId: number = 0;

  constructor(
    protected kendoGridService: KendoGridService,
    public accessService: AccessService,
    private salesService: SalesService,
    private campaignService: CampaignService,
    private formBuilder: FormBuilder,
    private templatesService: TemplatesService,
    private translateService: TranslateService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    super(kendoGridService);
  }

  ngOnInit() {
    log.debug('CampaignCreateComponent init');
    this.state = new DataSet().addSort('created_at', 'desc');
    const storageFilter = CustomKendoFilter.getStateFromStorage('CampaignSalesComponent');
    if (storageFilter) {
      this.state = storageFilter;
    }
    this.loadData(this.state);
    this.template_types = [
      { id: this.templatesTypes.SALEGENERAL, name: this.translateService.instant('EMAIL_TEMPLATES_BUTTON.GENERAL') },
      { id: this.templatesTypes.SALECLIENTOFFER, name: this.translateService.instant('EMAIL_TEMPLATES_BUTTON.OFFERS') },
      {
        id: this.templatesTypes.SALEWEBOFFERS,
        name: this.translateService.instant('EMAIL_TEMPLATES_BUTTON.WEB_OFFERS'),
      },
      { id: this.templatesTypes.SALEINVOICES, name: this.translateService.instant('EMAIL_TEMPLATES_BUTTON.INVOICES') },
      { id: this.templatesTypes.SALEVOUCHERS, name: this.translateService.instant('EMAIL_TEMPLATES_BUTTON.VOUCHERS') },
      { id: this.templatesTypes.SALEFEEDBACK, name: this.translateService.instant('EMAIL_TEMPLATES_BUTTON.FEEDBACK') },
      {
        id: this.templatesTypes.SALESENDTERMS,
        name: this.translateService.instant('EMAIL_TEMPLATES_BUTTON.SEND_TERMS'),
      },
      {
        id: this.templatesTypes.SALEAUTOCAMPAIGN,
        name: this.translateService.instant('EMAIL_TEMPLATES_BUTTON.AUTO_CAMPAIGN'),
      },
    ];
    this.emptySale.note = 'mass';

    this.route.queryParamMap.subscribe((params) => {
      if (params.get('resend')) {
        this.campaignId = Number(params.get('resend'));
        this.loadCampaign(this.campaignId);
      } else {
        let sales = JSON.parse(window.localStorage.getItem('CAMPAIGN_SELECTED_SALES'));
        if (sales) {
          sales.forEach((sale: any) => {
            this.selectedSales.push(new Sale().deserialize(sale));
            this.selectedSalesIds.push(sale.id);
          });
        }
      }
    });
  }

  public mySelectionKey(context: RowArgs): string {
    return context.dataItem.id;
  }

  clearFilters() {
    this.router.navigate(['/campaign/create']);
    this.apiQuery = new ApiQuery();
    this.loadData();
  }

  loadData(state: DataStateChangeEvent = null) {
    log.debug('loadData', state);
    this.loader = true;
    this.state = state;
    this.apiQuery
      .setDataSetFilters(state)
      .addIncludes(
        'company',
        'event',
        'event.event_group',
        'client.country',
        'client.city',
        'client.tags',
        'sales_agent',
        'customer_care_agent',
        'reservation_agent',
        'status',
        'channel',
        'campaign',
        'tags',
        'requests.type',
        'requests.status',
        'assign_to_user',
        'assign_to_role',
        'arrivalRequest',
        'departureRequest',
        'source',
        'finals',
        'finals.sale_offer',
        'finals.items',
        'finals.items.hotel_booking_room.hotel',
        'finals.items.hotel_booking_room.type'
      );

    this.salesService.index(this.apiQuery).subscribe((response: PaginatedResponse) => {
      response.data.forEach((sale: Sale) => {
        sale['meta_front'] = {
          clientTime: null,
          clockInterval: null,
        };

        if (sale.client && sale.client.city && sale.client.city.timezone) {
          clearInterval(sale.meta_front.clockInterval);
          if (sale.client.city.timezone) {
            sale.meta_front.clockInterval = setInterval(() => {
              sale.meta_front.clientTime = moment().tz(sale.client.city.timezone).format('HH:mm:ss');
            }, 1000);
          }
        } else {
          clearInterval(sale.meta_front.clockInterval);
          sale.meta_front.clockInterval = 'false';
        }
        let sale_final_hotels = new Array<string>();
        let sale_final_room_types = new Array<string>();
        sale.finals.forEach((sf: SaleFinal) => {
          sf.items.forEach((sf_item) => {
            sale_final_hotels.push(sf_item.hotel_booking_room.hotel.name);
            sale_final_room_types.push(sf_item.hotel_booking_room.type.value);
          });
        });
        sale_final_hotels = sale_final_hotels.filter((x, i, a) => a.indexOf(x) == i);
        sale['final_hotels'] = sale_final_hotels.join(', ');

        sale_final_room_types = sale_final_room_types.filter((x, i, a) => a.indexOf(x) == i);
        sale['final_room_types'] = sale_final_room_types.join(', ');
      });
      this.requestsResponse = response;
      this.data = response.getGridData();
      this.loader = false;
    });

    CustomKendoFilter.pushStateInStorage('CampaignSalesComponent', this.state);
  }

  loadCampaign(id: number) {
    const apiQuery: ApiQuery = new ApiQuery().addIncludes(
      'event',
      'event.event_group',
      'sales',
      'sales.sale',
      'creator',
      'template',
      'sales.sale.requests.type',
      'sales.sale.requests.status',
      'sales.sale.client.country',
      'sales.sale.client.city',
      'sales.sale.client.tags',
      'sales.sale.event',
      'sales.sale.event.event_group',
      'sales.sale.sales_agent',
      'sales.sale.customer_care_agent',
      'sales.sale.reservation_agent',
      'sales.sale.company',
      'sales.sale.status',
      'sales.sale.source',
      'sales.sale.channel',
      'sales.sale.campaign',
      'sales.sale.tags',
      'sales.sale.assign_to_user',
      'sales.sale.assign_to_role',
      'sales.sale.arrivalRequest',
      'sales.sale.departureRequest'
    );
    this.campaignService.show(id, apiQuery).subscribe((campaign: Campaign) => {
      this.selectedSales = [];
      this.selectedSalesIds = [];
      campaign.sales.forEach((cs, index) => {
        this.selectedSales.push(cs.sale);
        this.selectedSalesIds.push(cs.sale.id);
      });
    });
  }

  selectedSalesChange(selectedKeys: number[]) {
    this.selectedSalesIds = selectedKeys || [];

    // версията на кендо грид, с която се работи в момента няма функционалност за автоматично отваряне на вс details редове
    // this.selectedSalesIds.forEach((item, idx) => {

    //   grid.expandRow(idx);
    // });
    let ss = this.selectedSales;
    this.selectedSales = [];
    this.selectedSalesIds.forEach((id, index) => {
      let selectedRow1 = ss.find((sale: Sale) => {
        return sale.id == id;
      });
      if (selectedRow1) {
        this.selectedSales.push(selectedRow1);
      } else {
        let selectedRow = this.requestsResponse.data.find((sale: Sale) => {
          return sale.id == id;
        });
        this.selectedSales.push(selectedRow);
      }
    });
    window.localStorage.setItem('CAMPAIGN_SELECTED_SALES', JSON.stringify(this.selectedSales));
  }

  removeSelectedSale(selectedSale: Sale) {
    const index = this.selectedSales.indexOf(selectedSale);
    this.selectedSales.splice(index, 1);

    const indexId = this.selectedSalesIds.indexOf(selectedSale.id);
    this.selectedSalesIds.splice(indexId, 1);

    window.localStorage.setItem('CAMPAIGN_SELECTED_SALES', JSON.stringify(this.selectedSales));
  }

  stepTwoSelectedSales() {
    let emptyArr = new Array();
    emptyArr.push(this.emptySale);
    return this.selectedSales.concat(emptyArr);
  }

  setCurrentStep(number: number) {
    this.currentStep = number;
    if (number == 2) {
      this.createForm();
      this.createFilterForm();
      this.selectedEvent = null;
      //this.loadTemplates();

      setTimeout(() => {
        this.selectedSales.forEach((sale, index) => {
          this.form.get('requests_' + index + '_sales_agent_id').setValue(sale['sales_agent_id']);
          this.form.get('requests_' + index + '_sales_agent').setValue(sale['sales_agent']);
          this.form.get('requests_' + index + '_reservation_agent_id').setValue(sale['reservation_agent_id']);
          this.form.get('requests_' + index + '_reservation_agent').setValue(sale['reservation_agent']);
          this.form.get('requests_' + index + '_customer_care_agent_id').setValue(sale['customer_care_agent_id']);
          this.form.get('requests_' + index + '_customer_care_agent').setValue(sale['customer_care_agent']);
          this.form.get('requests_' + index + '_id').setValue(sale['id']);
        });
      }, 50);
    }
  }

  private createForm() {
    this.form = this.formBuilder.group({
      batch: [1],
      campaign_id: [null],
      event_id: [null, [Validators.required]],
      subject: [null, [Validators.required]],
      body: ['', [Validators.required]],
      template_id: [''],
      add_sale: [true],
      add_rl: [false],
      latest_wo: [false],
      newest_wo: [false],
      mass_assign_reservation_agent_id: [null],
      mass_assign_sales_agent_id: [null],
      mass_assign_customer_care_agent_id: [null],
      mass_assign_reservation_agent: [null],
      mass_assign_sales_agent: [null],
      mass_assign_customer_care_agent: [null],
    });

    this.selectedSales.forEach((sale, index) => {
      this.form.addControl('requests_' + index + '_sales_agent_id', new FormControl(null, Validators.required));
      this.form.addControl('requests_' + index + '_sales_agent', new FormControl(null, Validators.required));
      this.form.addControl('requests_' + index + '_reservation_agent_id', new FormControl(null, Validators.required));
      this.form.addControl('requests_' + index + '_reservation_agent', new FormControl(null, Validators.required));
      this.form.addControl('requests_' + index + '_customer_care_agent_id', new FormControl(null));
      this.form.addControl('requests_' + index + '_customer_care_agent', new FormControl(null));
      this.form.addControl('requests_' + index + '_id', new FormControl(null, Validators.required));
    });
  }

  fillMassControls(toControlName: string): void {
    this.selectedSales.forEach((sale, index) => {
      this.form
        .get('requests_' + index + '_' + toControlName + '_id')
        .setValue(this.form.get('mass_assign_' + toControlName + '_id').value);
      this.form
        .get('requests_' + index + '_' + toControlName)
        .setValue(this.form.get('mass_assign_' + toControlName).value);
    });
  }

  select(event: any) {
    this.selectedEvent = event;
  }

  loadTemplates(value: any) {
    this.templateTypeId = value.id;
    const query = new ApiQuery()
      .setLimit(9999)
      .addFilter('type_id', this.templateTypeId)
      .addIncludes('company', 'locale');

    this.templatesService.index(query).subscribe((response: PaginatedResponse) => {
      this.templates = response.data;

      this.templates.forEach((template: EmailTemplate) => {
        log.debug('forEach template');
        log.debug('this.companies', this.companies);
        // предотвратява дублиране
        if (this.companies.map((x) => x.id).indexOf(template.company_id) === -1 && template.company) {
          log.debug('push company', template);
          this.companies = [...this.companies, template.company];
        }
        // предотвратява дублиране
        if (this.locales.map((x) => x.id).indexOf(template.locale_id) === -1) {
          this.locales = [...this.locales, template.locale];
        }
      });
      this.filterTemplates();
    });
    this.nullifyFilterForm();
  }

  filterTemplates() {
    const filterObj: any = {
      company_id: null,
      locale_id: null,
    };

    if (this.filterForm.get('company_id').value) {
      filterObj.company_id = this.filterForm.get('company_id').value;
    }

    if (this.filterForm.get('locale_id').value) {
      filterObj.locale_id = this.filterForm.get('locale_id').value;
    }

    this.filteredTemplates = this.templates.filter((template: EmailTemplate) => {
      if (filterObj.company_id && filterObj.locale_id) {
        return template.company_id === filterObj.company_id && template.locale_id === filterObj.locale_id;
      }

      if (filterObj.company_id && !filterObj.locale_id) {
        return template.company_id === filterObj.company_id;
      }

      if (!filterObj.company_id && filterObj.locale_id) {
        return template.locale_id === filterObj.locale_id;
      }
      return template;
    });
    this.filterForm.get('template_id').reset();
  }

  private createFilterForm(): void {
    this.filterForm = this.formBuilder.group({
      //  company_id: [null, [Validators.required]],
      company_id: [null],
      locale_id: [null, [Validators.required]],
      template_id: [null, [Validators.required]],
      event_id: [null],
      template_type_id: [null],
      newest_wo: [false],
      latest_wo: [false],
    });
  }

  private nullifyFilterForm(): void {
    this.filterForm.controls['company_id'].setValue(null);
    this.filterForm.controls['locale_id'].setValue(null);
    this.filterForm.controls['template_id'].setValue(null);
    this.filterForm.controls['newest_wo'].setValue(false);
    this.filterForm.controls['latest_wo'].setValue(false);
  }

  compile() {
    let eventId = 0;
    if (this.form.get('event_id').value > 0) {
      eventId = this.form.get('event_id').value;
    }

    let formattedFilterDataObj: any = {
      template_id: this.filterForm.get('template_id').value,
      object_id: eventId,
      filters: {
        sale_offer_ids: null,
        sale_request_ids: null,
        sale_invoice_ids: null,
        sale_final_ids: null,
        sale_web_offer_ids: null,
        media_id: null,
        newest_wo: null,
        latest_wo: null,
      },
    };
   
    if (this.filterForm.get('template_type_id').value === this.templatesTypes.SALEWEBOFFERS) {
      formattedFilterDataObj.filters.newest_wo = this.filterForm.get('newest_wo').value;
      formattedFilterDataObj.filters.latest_wo = this.filterForm.get('latest_wo').value;
    }

    const formattedFilterData = new EmailTemplateCompiler().deserialize(formattedFilterDataObj);

    this.templatesService.compileRaw(formattedFilterData).subscribe((res) => {
      this.form.get('body').patchValue(res.compiled.html_template);
      this.form.get('subject').patchValue(res.compiled.subject);

      let template = this.filteredTemplates.filter((template: EmailTemplate) => {
        return template.id == this.filterForm.get('template_id').value;
      });

      this.form.get('template_id').patchValue(template[0].id);

      //this.attachSignature();
    });
  }

  getFileObj(base64: any = null, fileName: string = null, checked: boolean = true): any {
    return {
      base64: base64,
      file_name: fileName,
      checked: checked,
    };
  }

  attachSignature() {
    let sig = '';
    const currSelectedEmail = this.form.get('from').value;
    if (currSelectedEmail) {
      this.senderSmtpSetting.forEach((smtp: SmtpSettings) => {
        if (smtp.from_email === currSelectedEmail) {
          sig = smtp.signature;
        }
      });
    }

    const bodyHtml = $('<div>' + this.form.get('body').value + '</div>');

    if (bodyHtml.find('.signature').length > 0) {
      bodyHtml.find('.signature').html(sig);
    } else {
      bodyHtml.append('<div class="signature">' + sig + '</div>');
    }

    this.form.get('body').patchValue(bodyHtml.html());
  }

  sendEmail() {
    let stringErr = '';
    let string = '';
    let countSuccess = 0;

    if (this.filterForm.get('template_type_id').value === this.templatesTypes.SALEWEBOFFERS) {
      this.form.get('newest_wo').patchValue(this.filterForm.get('newest_wo').value);
      this.form.get('latest_wo').patchValue(this.filterForm.get('latest_wo').value);
    }

    const sendCampaign = () => {
      this.campaignService.send(this.form.value).subscribe(
        (res: any) => {
          let failedIdsMail: Array<number> = [];
          res.data.entries.forEach((item: any, index: number) => {
            if (item.success) {
              if (item.add_sale) {
                string =
                  string +
                  this.translateService.instant('CAMPAIGN.INFO.COPY') +
                  ' ' +
                  item.id +
                  ' ' +
                  this.translateService.instant('CAMPAIGN.INFO.COPY_CREATED') +
                  '<br />';
              } else {
                string =
                  string +
                  this.translateService.instant('CAMPAIGN.INFO.PROCCESSED') +
                  ' ' +
                  item.id +
                  ' ' +
                  this.translateService.instant('CAMPAIGN.INFO.PROCCESSED_CREATED') +
                  '<br />';
              }
            } else {
              if (item.reason == 'duplicate') {
                stringErr =
                  stringErr +
                  this.translateService.instant('CAMPAIGN.INFO.COPY') +
                  ' ' +
                  item.id +
                  ' ' +
                  this.translateService.instant('CAMPAIGN.INFO.FAILED_DUPLICATE') +
                  '<br />';
              } else if (item.reason == 'email') {
                failedIdsMail.push(item.id);
                stringErr =
                  stringErr +
                  this.translateService.instant('CAMPAIGN.INFO.COPY') +
                  ' ' +
                  item.id +
                  ' ' +
                  this.translateService.instant('CAMPAIGN.INFO.FAILED_EMAIL') +
                  ' ' +
                  item.message +
                  '<br />';
              } else if (item.reason == 'already_sale') {
                stringErr =
                  stringErr +
                  this.translateService.instant('CAMPAIGN.INFO.COPY') +
                  ' ' +
                  item.id +
                  ' ' +
                  this.translateService.instant('CAMPAIGN.INFO.FAILED_SUCH_SALE') +
                  ' ' +
                  item.such_sale +
                  '<br />';
              } else if (item.reason == 'mail_unactive') {
                stringErr =
                  stringErr +
                  this.translateService.instant('CAMPAIGN.INFO.COPY') +
                  ' ' +
                  item.id +
                  ' ' +
                  this.translateService.instant('CAMPAIGN.INFO.FAILED_MAIL_UNACTIVE') +
                  '<br />';
              } else {
                stringErr =
                  stringErr +
                  this.translateService.instant('CAMPAIGN.INFO.COPY') +
                  ' ' +
                  item.id +
                  ' ' +
                  this.translateService.instant('CAMPAIGN.INFO.FAILED_EMAIL') +
                  ' ' +
                  item.message +
                  '<br />';
              }
            }
          });

          this.selectedSalesIds = this.selectedSalesIds.filter((id) => failedIdsMail.includes(id));
          this.selectedSales = this.selectedSales.filter((item) => failedIdsMail.includes(item.id));

          countSuccess = countSuccess + res.data.copy;

          if (res.data.batch_complete) {
            this.loadData(this.state);
            this.currentStep = 1;

            string =
              countSuccess + ' ' + this.translateService.instant('CAMPAIGN.INFO.CREATED') + '<br /><br />' + string;

            window.localStorage.setItem('CAMPAIGN_SELECTED_SALES', JSON.stringify(this.selectedSales));

            this.toastr.success(string, '', { enableHtml: true, disableTimeOut: true });
            if (stringErr) {
              this.toastr.error(stringErr, '', { enableHtml: true, disableTimeOut: true });
            }
          } else {
            this.form.get('batch').setValue(this.form.get('batch').value + 1);
            this.form.get('campaign_id').setValue(res.data.campaign_id);

            sendCampaign();
          }
        },
        (error) => {
          stringErr = 'An error occured of creating a campaign';

          this.loadData(this.state);
          this.currentStep = 1;

          window.localStorage.setItem('CAMPAIGN_SELECTED_SALES', JSON.stringify(this.selectedSales));

          if (string) {
            this.toastr.success(string, '', { enableHtml: true, disableTimeOut: true });
          }
          if (stringErr) {
            this.toastr.error(stringErr, '', { enableHtml: true, disableTimeOut: true });
          }
        }
      );
    };

    sendCampaign();
  }
}
